// Programmatic in-app navigation.
//
// The single top-level Router instance (installed by the app shell) intercepts
// <a> clicks and popstate on its own; this module covers the code-driven cases —
// the auth guard's redirect, "go home" from the not-found page, routing into a
// resource the caller just created — and keeps the URL bar and the router in
// sync for them.
//
// Ported from bino-cloud (`cloud/ui/src/nav.ts`), with `hrefFor` added because
// this product's URLs nest three levels deep (instance → surface → object) and
// hand-built template strings in fourteen page modules is how a route typo ships.

import type { Routes } from "@lit-labs/router";

let routerRef: Routes | undefined;

/** bindRouter registers the shell's Router so [navigate] can drive it. Called
 * once, from the app shell's `connectedCallback`. */
export function bindRouter(r: Routes): void {
  routerRef = r;
}

/**
 * navigate performs an in-app navigation to a path, which may include a query
 * string. It updates history and drives the router's `goto` — which matches on
 * the pathname only, so pages read `location.search` themselves.
 *
 *   navigate("/instances/" + id);
 *   navigate("/sign-in?next=" + encodeURIComponent(location.pathname), { replace: true });
 *
 * Navigating to the current location is a no-op rather than a history entry, so
 * a guard that redirects to where the user already is cannot build a back-button
 * trap.
 */
export function navigate(path: string, opts: { replace?: boolean } = {}): void {
  const url = new URL(path, location.origin);
  const target = url.pathname + url.search;
  if (target === location.pathname + location.search) return;
  if (opts.replace) {
    history.replaceState({}, "", target);
  } else {
    history.pushState({}, "", target);
  }
  void routerRef?.goto(url.pathname);
}

/**
 * hrefFor builds an in-app path from segments, encoding each one. Use it
 * wherever a record id or a policy name goes into a URL — a policy `name` is
 * customer-controlled text and a datasource name may contain characters that
 * would otherwise change the shape of the path.
 *
 *   hrefFor("instances", instanceId, "policies", kind, name)
 *   // "/instances/k3x9q1m7p2vb8dn/policies/ColumnMaskPolicy/mask-pii"
 */
export function hrefFor(...segments: Array<string | number>): string {
  return "/" + segments.map((s) => encodeURIComponent(String(s))).join("/");
}

/**
 * isActive reports whether `path` is the current location, or a prefix of it.
 * The exact-match branch for "/" exists because every path starts with it, so a
 * prefix test would light up the home tab on every screen in the product.
 */
export function isActive(path: string): boolean {
  const here = location.pathname;
  if (path === "/") return here === "/";
  return here === path || here.startsWith(path + "/");
}
